if (!customElements.get('sa-contact')) {
  class SaContact extends HTMLElement {
    constructor() {
      super();
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleProductClick = this.handleProductClick.bind(this);
    }

    connectedCallback() {
      // Use event delegation with CAPTURE phase to ensure we intercept the event
      // before any other listeners on the form or bubbling listeners can interfere.
      this.addEventListener('submit', this.handleSubmit, true);
      
      // Product interest buttons
      this.productBtns = this.querySelectorAll('.sa-contact__product-btn');
      this.productBtns.forEach(btn => {
        btn.addEventListener('click', this.handleProductClick);
      });
      
      // Cache message elements
      this.popupMessage = this.querySelector('.sa-popup-message');
      
      // Hide messages on load (in case browser cached the state)
      this.hideMessages();

      // Real-time validation: Clear error state on input interaction
      this.querySelectorAll('input, select, textarea').forEach(input => {
        ['input', 'change'].forEach(evt => {
          input.addEventListener(evt, () => {
            const wrapper = input.closest('.sa-contact__field');
            if (wrapper) wrapper.classList.remove('sa-contact__field--error');
          });
        });
      });
    }

    disconnectedCallback() {
      this.removeEventListener('submit', this.handleSubmit, true);
      
      if (this.productBtns) {
        this.productBtns.forEach(btn => {
          btn.removeEventListener('click', this.handleProductClick);
        });
      }
    }

    handleProductClick(e) {
      e.preventDefault();
      e.currentTarget.classList.toggle('sa-contact__product-btn--active');
      this.updateHiddenProducts();
      
      // Clear error if exists
      const wrapper = this.querySelector('#ContactForm-products')?.closest('.sa-contact__field');
      if (wrapper) wrapper.classList.remove('sa-contact__field--error');
    }

    validateForm() {
      let isValid = true;
      let firstInvalid = null;

      // 1. Check fields with .sa-contact__field wrapper (Inputs, Selects, Products)
      const fields = this.querySelectorAll('.sa-contact__field');
      
      fields.forEach(field => {
        const input = field.querySelector('input[required], select[required], textarea[required]');
        const isProductField = field.querySelector('#ContactForm-products'); // Hidden input for products
        
        let isFieldValid = true;
        
        if (isProductField) {
           // Custom check for product buttons
           const activeProducts = this.querySelectorAll('.sa-contact__product-btn--active');
           if (activeProducts.length === 0) isFieldValid = false;
        } else if (input) {
           // Standard check for text/email/tel/select
           const val = input.value.trim();
           
           if (!val) {
             isFieldValid = false;
             // Restore default error message
             const errorText = field.querySelector('.sa-contact__error-text');
             if (errorText) errorText.textContent = 'This field is required';
           } else if (input.type === 'email') {
             // Email format validation
             const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
             if (!emailRegex.test(val)) {
               isFieldValid = false;
               // Set custom error message for invalid email
               const errorText = field.querySelector('.sa-contact__error-text');
               if (errorText) errorText.textContent = 'Please enter a valid email address';
             }
           }
        } else {
            // Optional field or no required input found
            return;
        }

        if (!isFieldValid) {
            isValid = false;
            field.classList.add('sa-contact__field--error');
            if (!firstInvalid) firstInvalid = field;
        } else {
            field.classList.remove('sa-contact__field--error');
        }
      });
      
      // 2. Check Checkbox (Terms) - No inline error UI, but required
      const terms = this.querySelector('#ContactForm-terms');
      if (terms && !terms.checked) {
          isValid = false;
          if (!firstInvalid) {
             this.showError('Please agree to the terms and conditions.');
             return false;
          }
      }

      if (firstInvalid) {
        // Scroll to the first error
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Focus if possible
        const input = firstInvalid.querySelector('input:not([type="hidden"]), select, textarea');
        if (input) input.focus();
        
        return false;
      }

      return isValid;
    }

    updateHiddenProducts() {
      const activeProducts = Array.from(this.querySelectorAll('.sa-contact__product-btn--active'))
        .map(btn => btn.dataset.value);
      const hiddenInput = this.querySelector('#ContactForm-products');
      if (hiddenInput) {
        hiddenInput.value = activeProducts.join(', ');
      }
    }

    async getIpInfo() {
      let ipInfo = {}
      try {
        ipInfo = await fetch('https://ipapi.co/json/').then(r => r.json())
      } catch(e) {
        console.log(e)
      }
      return ipInfo;
    }

    async handleSubmit(e) {
      e.preventDefault();
      e.stopPropagation(); 
      e.stopImmediatePropagation(); // Ensure no other listeners on the same element run
      
      const submitBtn = this.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.classList.add('loading');
        const originalText = submitBtn.innerHTML;
        submitBtn.innerHTML = 'Sending...';
        submitBtn.disabled = true;
        this.submitBtn = submitBtn;
        this.originalBtnText = originalText;
      }

      // Hide previous messages
      this.hideMessages();

      // Validation
      if (!this.validateForm()) {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.innerHTML = this.originalBtnText;
          submitBtn.disabled = false;
        }
        return;
      }

      // Collect data using URLSearchParams for application/x-www-form-urlencoded
      // This helps avoid CORS preflight issues and 500 errors if the server expects form data
      const formData = new URLSearchParams();

      // Helper function to safely get value or empty string
      const getValue = (selector) => {
        const el = this.querySelector(selector);
        const val = el && el.value ? el.value.trim() : '';
        // If value is empty, send a space ' ' to ensure backend recognizes the field/avoids validation errors
        return val === '' ? ' ' : val;
      };

      formData.append('firstName', getValue('[name="contact[first_name]"]'));
      formData.append('lastName', getValue('[name="contact[last_name]"]'));
      formData.append('email', getValue('[name="contact[email]"]'));
      
      // Combine country and phone
      const country = getValue('[name="contact[country]"]');
      const phone = getValue('[name="contact[phone]"]');
      // For phone, we still trim because it's a combined field, but ensure at least space if result is empty
      const combinedPhone = (country + ' ' + phone).trim();
      formData.append('phone', combinedPhone === '' ? ' ' : combinedPhone);
      
      formData.append('organization', getValue('[name="contact[organization]"]'));
      formData.append('jobTitle', getValue('[name="contact[job_title]"]'));
      formData.append('message', getValue('[name="contact[body]"]'));

      // Subscribe field: yes/no
      const subscribeCheckbox = this.querySelector('[name="contact[subscribe]"]');
      formData.append('subscribe', subscribeCheckbox?.checked ? 'yes' : 'no');

      // Collect product types (multi-select, comma separated)
      const activeProducts = Array.from(this.querySelectorAll('.sa-contact__product-btn--active'))
        .map(btn => btn.dataset.value);
      const typeStr = activeProducts.join(', ');
      formData.append('type', typeStr === '' ? ' ' : typeStr);

      const ipInfo = await this.getIpInfo()
      if (ipInfo.ip) {
        formData.append('country_ip', ipInfo.ip);
      }
      if (ipInfo.country_name) {
        formData.append('country_name', ipInfo.country_name);
      }


      console.log('Submitting form data:', Object.fromEntries(formData));

      try {
        const response = await fetch('/apps/sharpa-contact/contact-submit?shop=sharpa-robot.myshopify.com', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formData.toString()
        });

        if (response.ok) {
           this.showSuccess();
           const form = e.target;
           if (form && typeof form.reset === 'function') {
             form.reset();
           } else {
             // Fallback if e.target is not the form (unlikely with submit event)
             const foundForm = this.querySelector('form');
             if (foundForm) foundForm.reset();
           }
           
           // Reset buttons
           this.querySelectorAll('.sa-contact__product-btn--active').forEach(btn => {
             btn.classList.remove('sa-contact__product-btn--active');
           });
        } else {
           throw new Error('Submission failed');
        }
      } catch (error) {
        console.error('Contact form submission error:', error);
        this.showError();
      } finally {
        if (submitBtn) {
          submitBtn.classList.remove('loading');
          submitBtn.innerHTML = this.originalBtnText;
          submitBtn.disabled = false;
        }
      }
    }

    hideMessages() {
      if (this.messageTimeout) {
        clearTimeout(this.messageTimeout);
        this.messageTimeout = null;
      }
      if (this.popupMessage) this.popupMessage.style.display = 'none';
      
      // Also hide default liquid messages if they exist
      const defaultSuccess = this.querySelector('.sa-contact__success');
      const defaultError = this.querySelector('.sa-contact__error');
      if (defaultSuccess) defaultSuccess.style.display = 'none';
      if (defaultError) defaultError.style.display = 'none';
    }

    showSuccess() {
      this.hideMessages(); // Clear any existing messages/timeouts
      if (this.popupMessage) {
        this.popupMessage.textContent = 'We have received your application and will contact you as soon as possible.';
        this.popupMessage.style.backgroundColor = '#1a1a1a'; // Dark background
        this.popupMessage.style.display = 'block';
        // Auto hide after 3 seconds
        this.messageTimeout = setTimeout(() => {
          this.hideMessages();
        }, 3000);
      }
    }

    showError(message) {
      this.hideMessages(); // Clear any existing messages/timeouts
      if (this.popupMessage) {
        this.popupMessage.textContent = message || 'Submission failed. Please try again.';
        this.popupMessage.style.backgroundColor = '#E42F23'; // Red background for error
        this.popupMessage.style.display = 'block';
        // Auto hide after 3 seconds
        this.messageTimeout = setTimeout(() => {
          this.hideMessages();
        }, 3000);
      }
    }
  }
  
  customElements.define('sa-contact', SaContact);
}
